import { zodResolver } from "@hookform/resolvers/zod";
import { UseMutateAsyncFunction } from "@tanstack/react-query";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import { ErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import {
  handleInternalMessageSubmit,
  useFormInternalMessage,
} from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { TrainInfoForm } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import {
  SingleTrainFormSchema,
  TrickFormHookGroupedTrainFormSchema,
  singleTrainFormSchema,
  trainFormSchemaValidatorMessage,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInfoRequest } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/requestSchema";
import {
  defaultFormSchema,
  singleTrainFormToRequestBody,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { useInfrastructureInformationForTrain } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useInfrastructureInformationForTrain";
import {
  TrainInfoProvider,
  useTrainInfoContext,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/useTrainInfoContext";
import { OperationalInformationTrainResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/operationalInformationTrainResponse";
import { useOperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { FC, useEffect, useMemo } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import { Box } from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { InfoMessageWithRetryButton } from "shared/components/feedback/InfoMessageWithRetryButton";
import SkeletonLoader from "shared/components/feedback/SkeletonLoader/SkeletonLoader";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useDropsRole } from "stores/useDropsRole";

export type CommonSubTypeProps = {
  trainId: TrainIdentifier;
  infrastructureEvent?: InfrastructureResponse;
  isProductionDeviation?: boolean;
};

export type BatchSubTypeProps = {
  activeSeriesKey: keyof TrickFormHookGroupedTrainFormSchema;
  groupIndex: number;
};

type RequestBodyEdit = {
  type: "edit";
  body: TrainInfoRequest;
};

type RequestBodyCreate = {
  type: "create";
  body: TrainInfoRequest;
};

export type RequestBody = RequestBodyCreate | RequestBodyEdit;

type NetworkStatus = "error" | "pending" | "idle" | "success";

type TrainInfoCreateProps = {
  mode: "create";
  onSubmit: UseMutateAsyncFunction<
    OperationalInformationTrainResponse,
    Error,
    RequestBody
  >;
  formData?: undefined;
  formDataStatus?: undefined;
  uuid?: undefined;
  internalMessages?: InternalMessageResponse[];
};

type TrainInfoEditProps = {
  mode: "edit";
  onSubmit: UseMutateAsyncFunction<
    OperationalInformationTrainResponse,
    Error,
    RequestBody
  >;
  formData: SingleTrainFormSchema | undefined; // Not optional but can be undefined before network request resolves
  formDataStatus: NetworkStatus;
  uuid: string;
  internalMessages: InternalMessageResponse[] | undefined;
};

type TrainInfoModalProps = {
  onClose: () => void;
  submitStatus: NetworkStatus;
  trainInfoWithOpenState: string[];
  title?: string;
  submitButtonLabel?: string;
  selectedTrain: TrainIdentifier;
};

const RenderInfrastructureDataNoDataInfo = ({
  retryFn,
}: {
  retryFn: () => void;
}) => (
  <InfoMessageWithRetryButton
    message="Fikk ikke til å hente aktive infrastrukturhendelser. Du kan fortsatt opprette toghendelse, men ikke koble den til en infrastrukturhendelse"
    retryFn={retryFn}
  />
);

/**
 * Render the train info form modal, either for editing an existing train event or creating a new train event
 * @param selectedTrain
 * @param onSubmit callback to perform when the form is submitted. The form must be valid before this callback is called.
 * @param onClose callback to perform when closing the modal
 * @param submitStatus the status of the action being performed onSubmit. Typically, the status of
 *                     the create/edit mutation
 * @param formData when editing an existing Event pass the existing status as formData
 * @param formDataStatus the status of the request that fetches formData
 * @param mode indicates if a new Event is being created, or an existing Event is being edited
 * @param uuid the id of event currently being edited
 * @param trainInfoWithOpenState a list of open train Events for this train. Used to avoid duplicate Events
 * @param title override the title
 * @param submitButtonLabel override the label on the submit button
 */

const TrainInfoFormDialogContent: FC<
  TrainInfoModalProps & (TrainInfoCreateProps | TrainInfoEditProps)
> = ({
  selectedTrain,
  onSubmit,
  onClose,
  submitStatus,
  formData,
  formDataStatus = "success",
  mode,
  uuid,
  trainInfoWithOpenState,
  title,
  submitButtonLabel,
  internalMessages,
}) => {
  const { t } = useTranslation();
  const { role } = useDropsRole();

  const { data: opInfoTypes, status: opInfoTypesStatus } =
    useOperationalInformationTypes(t);

  const formMethods = useFormVerbose<SingleTrainFormSchema>({
    resolver: zodResolver(singleTrainFormSchema),
    defaultValues: formData ?? defaultFormSchema(role),
    values: formData, // the formState will be updated if and when formData is updated
  });

  const internalMessagesFormMethods = useFormInternalMessage(internalMessages);
  const onInternalMessagesSubmit = handleInternalMessageSubmit(
    internalMessagesFormMethods,
  );

  const { handleSubmit, control } = formMethods;

  // Watch the form state and fetch preview data when it changes
  const currentFormValues = useWatch({
    control,
    name: "trainForm",
  });

  const {
    data: infrastructureData,
    status: infrastructureDataStatus,
    refetch: infrastructureDataRetryFn,
  } = useInfrastructureInformationForTrain(selectedTrain);

  // Necessary for useTrainRouteBatch (Reused from TrainInfoBatchModal for consistency)
  const { setAllTrainIds } = useTrainInfoContext();
  useEffect(() => {
    setAllTrainIds([selectedTrain]);
  }, [selectedTrain]);

  const tooltip = useMemo(
    () => trainFormSchemaValidatorMessage(currentFormValues),
    [currentFormValues],
  );

  // Submitting the form
  const onSubmitForm = handleSubmit((data) => {
    const trainInfoBody = singleTrainFormToRequestBody(
      data.trainForm,
      selectedTrain,
    );
    if (!trainInfoBody) {
      return;
    }

    const requestBody: RequestBody = {
      type: mode,
      body: trainInfoBody,
    };

    void onSubmit(requestBody).then((response) =>
      onInternalMessagesSubmit(response, uuid),
    );
  });

  return (
    <FormProvider {...formMethods}>
      <ActionModal
        onSubmitTooltip={tooltip}
        title={
          title ??
          (mode === "create"
            ? `${t("Opprett")} ${t("toghendelse")}`
            : `${t("Endre")} ${t("toghendelse")}`)
        }
        actionTitle={
          submitButtonLabel ??
          (mode === "create"
            ? `${t("Opprett")} ${t("hendelse")}`
            : `${t("Endre")} ${t("hendelse")}`)
        }
        onClose={onClose}
        onSubmit={onSubmitForm}
        loading={submitStatus === "pending"}
        isSuccess={submitStatus === "success"}
        isError={submitStatus === "error"}
        successMessage={
          mode === "create"
            ? `${t("Hendelse opprettet")}`
            : `${t("Hendelse endret")}`
        }
        failureMessage={
          mode === "create"
            ? `${t("Kunne ikke opprette hendelse.")} ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`
            : `${t("Kunne ikke endre hendelse.")} ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`
        }
        internalMessagesFormMethods={internalMessagesFormMethods}
      >
        <ErrorBoundary>
          <Box w="100%" display="grid" gap="30px">
            {(opInfoTypesStatus === "pending" ||
              formDataStatus === "pending" ||
              infrastructureDataStatus === "pending") && (
              <SkeletonLoader skeletonType="trainInfoForm" />
            )}

            {infrastructureDataStatus === "error" && (
              <RenderInfrastructureDataNoDataInfo
                retryFn={infrastructureDataRetryFn}
              />
            )}

            {(opInfoTypesStatus === "error" || formDataStatus === "error") && (
              <FailureMessage />
            )}

            {opInfoTypesStatus === "success" &&
              formDataStatus === "success" &&
              infrastructureDataStatus !== "pending" && (
                <TrainInfoForm
                  mode={mode}
                  uuid={uuid}
                  selectedTrain={selectedTrain}
                  trainInfoWithOpenState={trainInfoWithOpenState}
                  opInfoTypes={opInfoTypes}
                  infrastructureData={infrastructureData}
                  currentFormValues={currentFormValues}
                  editingEventType={formData?.trainForm?.type}
                />
              )}
          </Box>
        </ErrorBoundary>
      </ActionModal>
    </FormProvider>
  );
};

const TrainInfoFormModal: FC<
  TrainInfoModalProps & (TrainInfoCreateProps | TrainInfoEditProps)
> = (props) => (
  <TrainInfoProvider>
    <TrainInfoFormDialogContent {...props} />
  </TrainInfoProvider>
);

export default TrainInfoFormModal;
